/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {colors} from '@/shell/plugins/colors';
import i18n from '@/shell/i18n/i18n';
// @ts-expect-error
import {EvaluationType} from '@proto/teamdev/licensing/c/evaluation/values_pb';

function t(relativeKey: string | number): string {
  return i18n.t(
      'lserv-app.shell.model.teamdev.licensing.EvaluationTypeExt.' + relativeKey
  ) as string;
}

/**
 * The data associated with each individual evaluation type.
 */
interface EvaluationTypeProps {
  icon: string;
  iconColor: string;
}

/**
 * A extension for the `EvaluationType` type, which enriches it with properties needed for
 * displaying it in the UI.
 */
export class EvaluationTypeExt {

  /**
   * Returns an instance of `EvaluationType` that corresponds to the given `EvaluationType`.
   */
  static of(evaluationType: EvaluationType): EvaluationTypeExt {
    return new EvaluationTypeExt(evaluationType);
  }

  private static propsByEvaluationTypes = new Map<EvaluationType, EvaluationTypeProps>([
    [EvaluationType.ACADEMIC, {
      icon: 'license_academic',
      iconColor: colors.grayIcons
    }],
    [EvaluationType.OPEN_SOURCE, {
      icon: 'license_open_source',
      iconColor: colors.grayIcons
    }],
    [EvaluationType.COMMERCIAL, {
      icon: 'license_commercial',
      iconColor: colors.primary
    }]
  ]);

  private readonly evaluationType: EvaluationType;
  private readonly props: EvaluationTypeProps;

  private constructor(evaluationType: EvaluationType) {
    this.evaluationType = evaluationType;

    const props = EvaluationTypeExt.propsByEvaluationTypes.get(this.evaluationType);
    if (props === undefined) {
      throw new Error('Unknown evaluationType: ' + evaluationType);
    }

    this.props = props;
  }

  /**
   * A human-readable name of this evaluation type.
   */
  get name(): string {
    return t('EvaluationType.' + this.evaluationType);
  }

  /**
   * A short version of human-readable name of this evaluation type
   * (e.g. "Commercial" instead of "Commercial evaluation").
   */
  get shortName(): string {
    return t('EvaluationType_short.' + this.evaluationType);
  }

  /**
   * Icon name for this evaluation type.
   */
  get icon(): string {
    return this.props.icon;
  }

  /**
   * Color of the icon (see the `icon` property).
   */
  get iconColor(): string {
    return this.props.iconColor;
  }
}
